import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { ResumeSGChargeEntity } from "domain/entity/ResumeSGCharge/ResumeSGChargeEntity";
import { EMPTY_RESUME_SG_CHARGE_SEARCH_CRITERIA, ResumeSGChargeSearchCriteria } from "domain/entity/ResumeSGCharge/ResumeSGChargeSearchCriteria";
import { ConsortiumRepository } from "domain/repository/ChargeDetailMaintenance/ConsortiumRepo";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { ResumeSGChargeRepository } from "domain/repository/ResumeSGCharge/ResumeSGChargeRepo";
import _ from "lodash";
import { DropdownProps } from "presentation/model/DropdownProps";
import { ResumeSGChargeModel } from "presentation/model/ResumeSGCharge/ResumeSGChargeModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface ResumeSGChargeVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<ResumeSGChargeModel>> | ((value: SetStateAction<ResumeSGChargeModel>) => void),
    ],
    resumeSGChargeRepo: ResumeSGChargeRepository,
    chargeTypeRepo: ChargeTypeRepository,
    consortiumRepo: ConsortiumRepository,
}

export const ResumeSGChargeVM = ({ dispatch, resumeSGChargeRepo, chargeTypeRepo, consortiumRepo }: ResumeSGChargeVMProps) => {
    const [resumeSGChargeDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await chargeTypeRepo.getAllChargeTypesForCombobox().then(
            chargeTypes => {
                let newChargeTypeList = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
                //let newChargeTypes = newChargeTypeList.filter(entity => entity.chargeCategory === 'GEN' || entity.chargeCategory === 'SYS');

                let chargeTypeDropdownOption: DropdownProps[] = [];
                let subChargeTypeDropdownOption: { [key: string]: DropdownProps[] } = {};
                newChargeTypeList?.forEach(chgTypeEty => {
                    const chgTypeExisted = chargeTypeDropdownOption?.find(chgType =>
                        chgType.value === chgTypeEty.chargeType);
                    if (!chgTypeExisted) {
                        chargeTypeDropdownOption.push({
                            dropdownLabel: chgTypeEty.chargeType,
                            tagLabel: chgTypeEty.chargeType,
                            value: chgTypeEty.chargeType
                        })
                    }
                    if (chgTypeEty.subChargeType) {
                        if (!subChargeTypeDropdownOption[chgTypeEty.chargeType]) {
                            subChargeTypeDropdownOption[chgTypeEty.chargeType] = [];
                        }
                        subChargeTypeDropdownOption[chgTypeEty.chargeType].push({
                            dropdownLabel: chgTypeEty.subChargeType,
                            tagLabel: chgTypeEty.subChargeType,
                            value: chgTypeEty.subChargeType
                        });
                    }
                });

                resumeSGChargeDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        chargeTypeDropdownOptions: chargeTypeDropdownOption,
                        subChargeTypeDropdownOptions: subChargeTypeDropdownOption
                    }
                }))
            }
        )

        await consortiumRepo.getAllConsortiums().then(
            consortiums => {
                let consortiumCodeDropdownOptions = consortiums?.map((item) => ({
                    dropdownLabel: item.consortiumCode,
                    tagLabel: item.consortiumCode,
                    value: item.consortiumCode,
                })) ?? []
                consortiumCodeDropdownOptions = _.orderBy(consortiumCodeDropdownOptions, "dropdownLabel");

                resumeSGChargeDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        consortiumCodeDropdownOptions: consortiumCodeDropdownOptions,
                    }
                }))
            }
        );

    }

    const updateSelectedRows = async (selecedRows: ResumeSGChargeEntity[]) => {
        resumeSGChargeDispatch(prevState => {

            return {
                ...prevState,
                selectedRows: selecedRows,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onRowDoubleClick = async (row: ResumeSGChargeEntity) => {
        resumeSGChargeDispatch(prevState => {

            return {
                ...prevState,
                isShowEditPanel: true,
                currentRow: row,
            }
        })
    }

    const onSearchClick = async () => {
        resumeSGChargeDispatch(prevState => {

            return {
                ...prevState,
                isShowSearchPanel: !prevState.isShowSearchPanel,
            }
        })
    }

    const onResetClick = async (isAdd: boolean) => {
        resumeSGChargeDispatch(prevState => {

            return {
                ...prevState,
                currentEditRow: prevState.currentRow,
            }
        })
    }

    const onRemoveAllSearchCriteria = async () => {
        resumeSGChargeDispatch(prevState => {
            return {
                ...prevState, searchCriteria: { ...EMPTY_RESUME_SG_CHARGE_SEARCH_CRITERIA },
            }
        })
    }

    const onResume = async (dataKeyMap: { [key: number]: string }): Promise<ResponseEntity> => {
        return await resumeSGChargeRepo.onSubmit(dataKeyMap);
    }

    const onSearch = async (searchCriteria: ResumeSGChargeSearchCriteria) => {
        resumeSGChargeDispatch(prevState => {
            return {
                ...prevState,
                // resumeSGChargeList: [],
                searchCriteria: searchCriteria,
            }
        })
        await resumeSGChargeRepo.getResumeSGDatas(searchCriteria).then((data) => {
            resumeSGChargeDispatch(prevState => {
                return {
                    ...prevState,
                    resumeSGChargeList: data,
                }
            })
        }).catch((error) => {
            return [];
        })
    }

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        resumeSGChargeDispatch(prevState => {
            let subChargeType = prevState.searchCriteria.subChargeType;
            if (fieldKey === "chargeType" && fieldValue !== prevState.searchCriteria.chargeType) {
                subChargeType = null;
            }
            if (fieldKey === "subChargeType") {
                subChargeType = val;
            }
            if (fieldKey === "voyageCode" || fieldKey === "vesselCode") {
                val = (val as unknown as string)?.toUpperCase();
            }
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                    subChargeType: subChargeType,
                },
            }
        })
    }

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        resumeSGChargeDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        resumeSGChargeDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldKey]: val,
                },
            }
        })
    }

    const setIsSearchLoading = (isSearchLoading: boolean) => {
        resumeSGChargeDispatch(prevState => {
            return {
                ...prevState,
                isSearchLoading: isSearchLoading,
            }
        })
    }

    return {
        onFieldChange: onFieldChange,
        onResume: onResume,
        onDateRangeChange: onDateRangeChange,
        onSearchFieldChange: onSearchFieldChange,
        onRemoveAllSearchCriteria: onRemoveAllSearchCriteria,
        onSearch: onSearch,
        onSearchClick: onSearchClick,
        onResetClick: onResetClick,
        onRowDoubleClick: onRowDoubleClick,
        updateSelectedRows: updateSelectedRows,
        loadDropdownOption: loadDropdownOption,
        setIsSearchLoading: setIsSearchLoading,
    }
}